import React from 'react';
import { usePWA } from '../hooks/usePWA';
import { Download, X, WifiOff, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

// Install Banner Component - shows at top of page when app is installable
export const PWAInstallBanner = () => {
  const { showInstallBanner, install, dismissInstall } = usePWA();

  if (!showInstallBanner) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 shadow-lg animate-slide-down">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Smartphone className="w-5 h-5" />
          </div>
          <div>
            <p className="font-medium text-sm">Installer GestMob</p>
            <p className="text-xs text-blue-100">Accès rapide et mode hors-ligne</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={install}
            size="sm"
            className="bg-white text-blue-600 hover:bg-blue-50"
          >
            <Download className="w-4 h-4 mr-1" />
            Installer
          </Button>
          <button
            onClick={dismissInstall}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Offline Indicator - shows when user is offline
export const OfflineIndicator = () => {
  const { isOnline } = usePWA();

  if (isOnline) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50 flex items-center gap-2 bg-amber-500 text-white px-4 py-2 rounded-full shadow-lg animate-bounce-in">
      <WifiOff className="w-4 h-4" />
      <span className="text-sm font-medium">Mode hors-ligne</span>
    </div>
  );
};

// Install Button - can be placed anywhere in the app
export const PWAInstallButton = ({ className = '' }) => {
  const { isInstallable, isInstalled, install } = usePWA();

  if (isInstalled) {
    return (
      <div className={`flex items-center gap-2 text-emerald-600 text-sm ${className}`}>
        <Smartphone className="w-4 h-4" />
        <span>Application installée</span>
      </div>
    );
  }

  if (!isInstallable) {
    return null;
  }

  return (
    <Button
      onClick={install}
      variant="outline"
      className={`gap-2 ${className}`}
    >
      <Download className="w-4 h-4" />
      Installer l'application
    </Button>
  );
};

// Compact install prompt for mobile
export const PWAInstallPrompt = ({ onClose }) => {
  const { install } = usePWA();

  const handleInstall = async () => {
    await install();
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl animate-slide-up">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
            <img src="/icons/icon-96x96.png" alt="GestMob" className="w-12 h-12 rounded-xl" />
          </div>
        </div>
        
        <h3 className="text-xl font-bold text-center text-slate-900 mb-2">
          Installer GestMob
        </h3>
        <p className="text-center text-slate-500 text-sm mb-6">
          Installez l'application sur votre appareil pour un accès rapide et un fonctionnement hors-ligne.
        </p>
        
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3 text-sm text-slate-600">
            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span>Accès rapide depuis l'écran d'accueil</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-600">
            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span>Scanner disponible hors-ligne</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-600">
            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span>Données en cache pour consultation rapide</span>
          </div>
        </div>
        
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
          >
            Plus tard
          </Button>
          <Button
            className="flex-1 bg-blue-600 hover:bg-blue-700"
            onClick={handleInstall}
          >
            <Download className="w-4 h-4 mr-2" />
            Installer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallBanner;
